import { Tooltip, type TooltipProps } from "@mui/material";
import { useForkRef } from "@mui/material/utils";
import { cloneElement, type FocusEvent, type MouseEvent, type ReactElement, type Ref, type TouchEvent, useId, useRef } from "react";
import { MUI_TOOLTIP_TIMING } from "./lazyTooltipSupport";
import { TooltipAnchorProxy } from "./TooltipAnchorProxy";
import { useLazyTooltipTriggers } from "./useLazyTooltipTriggers";

/** What the lazy tooltip reads from, and adds to, its anchor: the listeners and names any Tooltip child takes. */
type AnchorProps = {
	ref?: Ref<HTMLElement>;
	onMouseOver?: (event: MouseEvent<HTMLElement>) => void;
	onMouseLeave?: (event: MouseEvent<HTMLElement>) => void;
	onFocus?: (event: FocusEvent<HTMLElement>) => void;
	onBlur?: (event: FocusEvent<HTMLElement>) => void;
	onTouchStart?: (event: TouchEvent<HTMLElement>) => void;
	onTouchEnd?: (event: TouchEvent<HTMLElement>) => void;
	"aria-label"?: string;
	"aria-labelledby"?: string;
};

/** The anchor's own listener runs first, then the tooltip's, as MUI composes them. */
const chain =
	<E,>(own: ((event: E) => void) | undefined, ours: (event: E) => void) =>
	(event: E) => {
		own?.(event);
		ours(event);
	};

/**
 * The anchor's accessible name, as MUI's Tooltip sets it: a string title names the anchor; any other title names it
 * by reference while open. The anchor's own `aria-label`/`aria-labelledby`, when it passes one, wins, as it does on MUI's.
 */
const namingProps = (title: TooltipProps["title"], own: AnchorProps, open: boolean, id: string): Pick<AnchorProps, "aria-label" | "aria-labelledby"> => ({
	...(!("aria-label" in own) && typeof title === "string" && { "aria-label": title }),
	...(!("aria-labelledby" in own) && open && typeof title !== "string" && { "aria-labelledby": id }),
});

/**
 * `InHouseTooltip` with `lazy`: the anchor is rendered as is, carrying MUI's listeners and name, and MUI's Tooltip is
 * mounted only the first time it opens. The popup is MUI's own (same look, placement, transition, arrow, Escape),
 * anchored to the real element, which is never re-mounted, so focus and hover stay where they are.
 */
export const LazyInHouseTooltip = ({
	children,
	title,
	id: idProp,
	enterDelay,
	leaveDelay,
	enterTouchDelay,
	leaveTouchDelay,
	slotProps,
	...rest
}: TooltipProps) => {
	const generatedId = useId();
	// DEFAULT: the caller's `id` when it passes one, as on MUI's Tooltip; else one of our own, to find the popup by.
	const id = idProp ?? generatedId;
	const anchorRef = useRef<HTMLElement | null>(null);
	// DEFAULT: each timing falls back to MUI's own default, so an unset one behaves exactly as on an eager tooltip.
	const timing = {
		enterDelay: enterDelay ?? MUI_TOOLTIP_TIMING.ENTER_DELAY,
		leaveDelay: leaveDelay ?? MUI_TOOLTIP_TIMING.LEAVE_DELAY,
		enterTouchDelay: enterTouchDelay ?? MUI_TOOLTIP_TIMING.ENTER_TOUCH_DELAY,
		leaveTouchDelay: leaveTouchDelay ?? MUI_TOOLTIP_TIMING.LEAVE_TOUCH_DELAY,
	};
	// MUI shows nothing for an empty title (anything falsy but 0), so neither does this.
	const canOpen = Boolean(title) || title === 0;
	const { phase, anchorListeners, onPopupOpen, onPopupClose } = useLazyTooltipTriggers({ timing, canOpen, anchorRef, popperId: id });

	// `as`: MUI types a Tooltip child as `ReactElement<unknown>`; these are the listeners every valid child already takes.
	const anchor = children as ReactElement<AnchorProps>;
	const own = anchor.props;
	const ref = useForkRef(own.ref, anchorRef);
	const popperProps = slotProps?.popper;
	if (typeof popperProps === "function") throw new Error("InHouseTooltip: a lazy tooltip's popper slotProps must be an object");

	return (
		<>
			{cloneElement(anchor, {
				...namingProps(title, own, phase === "open", id),
				ref,
				onMouseOver: chain(own.onMouseOver, anchorListeners.onMouseOver),
				onMouseLeave: chain(own.onMouseLeave, anchorListeners.onMouseLeave),
				onFocus: chain(own.onFocus, anchorListeners.onFocus),
				onBlur: chain(own.onBlur, anchorListeners.onBlur),
				onTouchStart: chain(own.onTouchStart, anchorListeners.onTouchStart),
				onTouchEnd: chain(own.onTouchEnd, anchorListeners.onTouchEnd),
			})}
			{phase !== "idle" && (
				<Tooltip
					{...rest}
					id={id}
					title={title}
					enterDelay={timing.enterDelay}
					leaveDelay={timing.leaveDelay}
					open={phase === "open"}
					onOpen={onPopupOpen}
					onClose={onPopupClose}
					slotProps={{
						...slotProps,
						popper: {
							...popperProps,
							anchorEl: () => {
								if (!anchorRef.current) throw new Error("InHouseTooltip: a lazy tooltip opened without its anchor");
								return anchorRef.current;
							},
						},
					}}
				>
					<TooltipAnchorProxy />
				</Tooltip>
			)}
		</>
	);
};
